import { defineStore } from 'pinia';
import { ref } from 'vue';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { getWsClient, type WebSocketControlEvent } from '@/services/WebSocketService';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organizationStore';
import { logger } from '@/utils/logger';

/**
 * Reacts to the server taking this tab's session away underneath it: another
 * tab switching organisation, an admin removing the user from the active one
 * or deleting it, or the user being deactivated or deleted. The websocket is
 * the only channel that hears about it — this tab's own changes close the
 * socket first and never get here.
 */
export const useSessionGuardStore = defineStore('sessionGuard', () => {
  /** The active organisation moved away from what this tab shows; the modal blocks until reload. */
  const organisationChanged = ref(false);

  let initialized = false;

  function initialize(): void {
    if (initialized || localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
      return;
    }
    initialized = true;
    getWsClient().addControlListener(onControlEvent);
  }

  function onControlEvent(event: WebSocketControlEvent): void {
    if (event.kind === 'access_revoked') {
      void signOutRevoked(event.reason);
      return;
    }
    // A tab that already reloaded into the new organisation has nothing stale.
    if (event.organisationUid === (useOrganizationStore().activeOrganization?.uid ?? null)) {
      return;
    }
    organisationChanged.value = true;
  }

  async function signOutRevoked(reason: string | null): Promise<void> {
    try {
      await useAuthStore().logout();
    } catch (error) {
      logger.error('Sign-out after revoked access failed:', error);
    }
    const query = reason === 'deactivated' || reason === 'deleted' ? reason : 'revoked';
    // A full load, like the sidebar's sign-out: every store goes with the session.
    window.location.assign(`/login?reason=${query}`);
  }

  return { organisationChanged, initialize };
});
